import { getFileDownloadURL } from './r2storage';

/**
 * Helper function to convert fileKeys to URLs in document data
 * Handles url, fileUrl, recordingUrl, videoUrl, attachmentUrl, materialUrl, pdfUrl, documentUrl fields
 */
export const convertFileKeysToUrls = async (data, urlFields = ['url', 'fileUrl', 'recordingUrl', 'videoUrl', 'attachmentUrl', 'materialUrl', 'pdfUrl', 'documentUrl']) => {
  if (!data || typeof data !== 'object') return data;
  
  const result = { ...data };
  const urlPromises = [];
  
  for (const field of urlFields) {
    if (result[field] && typeof result[field] === 'string') {
      // Check if it's a fileKey (doesn't start with http) or an expired URL
      if (!result[field].startsWith('http') || result[field].includes('X-Amz-Expires')) {
        let fileKey = result[field];
        
        // If it's an expired URL, extract the fileKey
        if (result[field].startsWith('http')) {
          try {
            const urlObj = new URL(result[field]);
            fileKey = decodeURIComponent(urlObj.pathname.substring(1)); // Remove leading /
          } catch (e) {
            console.warn(`Failed to parse URL for ${field}:`, result[field]);
          }
        }
        
        // Generate fresh URL
        urlPromises.push(
          getFileDownloadURL(fileKey)
            .then(urlResult => {
              if (urlResult.success) {
                result[field] = urlResult.url;
              }
            })
            .catch(error => {
              console.warn(`Failed to generate URL for ${field}:`, fileKey, error.message);
            })
        );
      }
    }
  }
  
  await Promise.all(urlPromises);
  return result;
};

/**
 * Helper function to convert fileKeys to URLs in an array of documents
 */
export const convertFileKeysToUrlsInArray = async (dataArray, urlFields = ['url', 'fileUrl', 'recordingUrl', 'videoUrl', 'attachmentUrl', 'materialUrl', 'pdfUrl', 'documentUrl']) => {
  if (!Array.isArray(dataArray)) return dataArray;
  
  const promises = dataArray.map(item => convertFileKeysToUrls(item, urlFields));
  return await Promise.all(promises);
};

/**
 * Wrapper function to add URL conversion to any collection getter
 * Usage: const getResourcesWithUrls = withUrlConversion(getResources);
 */
export const withUrlConversion = (getterFunction, urlFields) => {
  return async (...args) => {
    const result = await getterFunction(...args);
    
    if (!result.success || !result.data) {
      return result;
    }
    
    // Handle both single object and array
    if (Array.isArray(result.data)) {
      result.data = await convertFileKeysToUrlsInArray(result.data, urlFields);
    } else {
      result.data = await convertFileKeysToUrls(result.data, urlFields);
    }
    
    return result;
  };
};

export default {
  convertFileKeysToUrls,
  convertFileKeysToUrlsInArray,
  withUrlConversion
};
